import { useMemo } from "react";

import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { useGetUnsyncedCalendarSpacesQuery } from "../../services/calendar.service";
import type { RecommendedCardItem } from "../types/model/recommended";
import { toCalendarSyncItems } from "../utils/host-hub-calendar-sync.utils";

import useHostHubOwnerSpaces from "./useHostHubOwnerSpaces";

type UseHostHubUnsyncedCalendarSpacesResult = {
  isError: boolean;
  isLoading: boolean;
  spaces: RecommendedCardItem[];
};

/**
 * Fetches the host's unsynced spaces and derives the (capped) calendar-sync
 * rows for the "Keep your calendar up-to-date" recommendation.
 *
 * 1. `GET /v1/calendar/user/spaces/unsynced` → unsynced space IDs
 * 2. `GET /v1/spaces/owner/:sso-id?full=true` → space titles and images
 */
const useHostHubUnsyncedCalendarSpaces =
  (): UseHostHubUnsyncedCalendarSpacesResult => {
    const ssoId = useSelector(selectProfileSSOid);

    const ownerSpaces = useHostHubOwnerSpaces();

    const unsyncedSpacesQuery = useGetUnsyncedCalendarSpacesQuery(
      ssoId ? undefined : skipToken,
    );

    const spaces = useMemo(
      () =>
        toCalendarSyncItems({
          spaces: ownerSpaces.spaces,
          unsyncedSpaceIds: unsyncedSpacesQuery.data?.space_ids,
        }),
      [ownerSpaces.spaces, unsyncedSpacesQuery.data],
    );

    const isLoading = ownerSpaces.isLoading || unsyncedSpacesQuery.isLoading;

    const isError = ownerSpaces.isError || unsyncedSpacesQuery.isError;

    return {
      isError,
      isLoading,
      spaces,
    };
  };

export default useHostHubUnsyncedCalendarSpaces;
